<template>
	<div
		class="flex flex-col gap-3 font-Inter"
		data-testid="journey-popover"
	>
		<div class="flex items-center gap-2">
			<span class="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary p-1 text-primary-foreground">
				<component
					:is="icon"
					class="size-5"
					aria-hidden="true"
				/>
			</span>
			<div class="flex flex-col whitespace-nowrap">
				<p
					class="text-p-xs font-medium text-primary"
					data-testid="journey-popover-label"
				>
					{{ label }}
				</p>
				<p
					v-if="stage"
					class="text-p-sm font-medium text-gray-900"
					data-testid="journey-popover-stage"
				>
					{{ stage.name }}
				</p>
			</div>
		</div>

		<div class="flex flex-col">
			<h2
				id="journey-popover-headline"
				class="text-md font-medium text-gray-900"
				data-testid="journey-popover-headline"
			>
				{{ entry.headline(journey) }}
			</h2>
			<p
				class="text-p-sm text-gray-500"
				data-testid="journey-popover-detail"
			>
				<span
					v-for="part in entry.detail(journey)"
					:key="part.text"
					:class="{ 'font-medium': part.strong }"
				>{{ part.text }}</span>
			</p>
		</div>

		<div class="flex items-center gap-2">
			<Button
				v-if="showsPrimaryAction"
				as-child
				class="max-md:tap-target"
			>
				<RouterLink
					:to="action.to"
					data-testid="journey-popover-action"
					@click="emit('close')"
				>
					{{ action.label }}
				</RouterLink>
			</Button>
			<Button
				v-if="showsSeeYourJourney"
				as-child
				variant="ghost"
				class="bg-gray-100 text-gray-900 hover:bg-gray-200 max-md:tap-target"
			>
				<RouterLink
					:to="{ name: 'home' }"
					data-testid="journey-popover-see-journey"
					@click="emit('close')"
				>
					See Your Journey
				</RouterLink>
			</Button>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { Button } from '../ui/button';
import { focusIconFor, focusStateFor } from './focus-states';
import { JOURNEY_STAGES } from './stages';

/**
 * The header popover's body (EN-1024): the focus, its headline and a condensed detail line, then the
 * actions that make sense on the current page. The header owns opening and closing; this asks to
 * close when an action is taken.
 */
const props = defineProps({
	/** `GET /api/journey`, for a focus that has an entry in `FOCUS_STATES`. */
	journey: {
		type: Object,
		required: true,
	},
});

const emit = defineEmits(['close']);

const route = useRoute();

const entry = computed(() => focusStateFor(props.journey));
const stage = computed(() => JOURNEY_STAGES.find((candidate) => candidate.key === props.journey.focus.stage) ?? null);
const icon = computed(() => focusIconFor(props.journey));
const action = computed(() => entry.value.action(props.journey));

const label = computed(() => {
	if (entry.value.label) {
		return entry.value.label;
	}

	return props.journey.focus.source === 'user_choice' ? 'Your choice for today' : 'Today\'s Focus';
});

// Home is Your Journey itself, and the step's own page is where the primary action leads.
const showsPrimaryAction = computed(() => !entry.value.isOwnPage(route, props.journey));
const showsSeeYourJourney = computed(() => route.name !== 'home');
</script>
